import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  VIP_LEVEL_UP_TOAST_DATA,
  VIP_LEVEL_UP_TOAST_HANDLE,
  VipLevelUpToastComponent,
  VipLevelUpToastData,
} from './vip-level-up-toast.component.js';
import { UserViewService } from '../../core/view-states/user-view.service.js';

/**
 * Shows the {@link VipLevelUpToastComponent} in a top-right global CDK overlay.
 * Triggered from the user realtime channel on a `VipLevelUp` event so the
 * level-up notification surfaces regardless of the current route. No-ops on the
 * server (SSR) and auto-dismisses after a short window.
 */
@Injectable({ providedIn: 'root' })
export class VipLevelUpNotificationService {
  private readonly _overlay = inject(Overlay);
  private readonly _injector = inject(Injector);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _userView = inject(UserViewService);

  private readonly _autoDismissMs = 10_000;

  /** At most one toast at a time — replaced on each new level-up. */
  private _activeRef: OverlayRef | null = null;

  show(data: VipLevelUpToastData): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    // Never surface the level-up toast when the per-user vip kill-switch is
    // explicitly off — a stale session can still receive a VipLevelUp
    // realtime event after an admin disables the feature. null/undefined
    // (never toggled) mean enabled, mirroring killSwitchGuard.
    if (this._userView.state?.vip_enabled === false) {
      return;
    }

    // Replace any in-flight toast so rapid multi-level crossings don't stack
    // overlapping overlays at the same position.
    this._activeRef?.dispose();

    const overlayRef = this._overlay.create({
      positionStrategy: this._overlay.position().global().top('16px').right('16px'),
      hasBackdrop: false,
      panelClass: 'vip-level-up-toast-overlay',
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
    });
    this._activeRef = overlayRef;

    const injector = Injector.create({
      parent: this._injector,
      providers: [
        { provide: VIP_LEVEL_UP_TOAST_DATA, useValue: data },
        { provide: VIP_LEVEL_UP_TOAST_HANDLE, useValue: { dismiss: () => overlayRef.dispose() } },
      ],
    });

    overlayRef.attach(new ComponentPortal(VipLevelUpToastComponent, null, injector));

    const timer = setTimeout(() => overlayRef.dispose(), this._autoDismissMs);
    overlayRef.detachments().subscribe(() => {
      clearTimeout(timer);
      if (this._activeRef === overlayRef) {
        this._activeRef = null;
      }
    });
  }
}
